import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, PencilSimple, Trash, Package, ArrowsLeftRight } from "@phosphor-icons/react";
import { formatBRL } from "@/lib/format";
import { toast } from "sonner";

const empty = { name: "", base_value: "", recurrence: "mensal", description: "", client_switchable: false };

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const { data } = await api.get("/admin/plans"); setPlans(data); }
    catch { toast.error("Erro ao carregar planos"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, base_value: p.base_value, recurrence: p.recurrence, description: p.description || "", client_switchable: !!p.client_switchable });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, base_value: Number(form.base_value) };
    try {
      if (editing) await api.put(`/admin/plans/${editing.id}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success("Plano salvo");
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir plano "${p.name}"?`)) return;
    try { await api.delete(`/admin/plans/${p.id}`); toast.success("Plano removido"); load(); }
    catch { toast.error("Erro"); }
  };

  const recLabel = { mensal: "Mensal", trimestral: "Trimestral", semestral: "Semestral", anual: "Anual" };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Catálogo</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Planos</h1>
        </div>
        <Button onClick={openCreate} data-testid="plan-create-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus weight="bold" className="mr-2" size={16} /> Novo plano
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.length === 0 && (
          <Card className="border-dashed border-zinc-300 p-10 text-center text-zinc-500 col-span-full">
            <Package size={28} className="mx-auto mb-3 text-zinc-400" weight="duotone"/>
            Nenhum plano cadastrado. Clique em "Novo plano" para começar.
          </Card>
        )}
        {plans.map((p) => (
          <Card key={p.id} className="border-zinc-200 p-5 card-soft" data-testid={`plan-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] tracking-widest uppercase text-zinc-500">{recLabel[p.recurrence]}</div>
                <div className="font-display text-xl font-semibold mt-1">{p.name}</div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`}><PencilSimple size={16}/></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(p)} className="text-rose-600"><Trash size={16}/></Button>
              </div>
            </div>
            <div className="metric-num text-3xl mt-3">{formatBRL(p.base_value)}</div>
            {p.description && <div className="text-sm text-zinc-600 mt-2">{p.description}</div>}
            {p.client_switchable && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-semibold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">
                <ArrowsLeftRight size={11} weight="bold"/> Disponível para troca
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Editar plano" : "Novo plano"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <Field label="Nome" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="plan-form-name"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Valor (R$)" required>
                <Input type="number" step="0.01" value={form.base_value} onChange={(e) => setForm({ ...form, base_value: e.target.value })} required data-testid="plan-form-value"/>
              </Field>
              <Field label="Recorrência">
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger data-testid="plan-form-recurrence"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Descrição">
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}/>
            </Field>
            <label className="flex items-start gap-3 p-3 border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50">
              <input
                type="checkbox"
                checked={!!form.client_switchable}
                onChange={(e) => setForm({ ...form, client_switchable: e.target.checked })}
                data-testid="plan-form-switchable"
                className="mt-1 w-4 h-4 accent-blue-600"
              />
              <div className="flex-1">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <ArrowsLeftRight size={14} weight="bold" className="text-blue-600"/> Disponível para troca pelo cliente
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Quando marcado, este plano aparecerá no dashboard dos clientes como opção de troca. A troca só é efetivada após pagamento aprovado.
                </div>
              </div>
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="plan-form-submit" className="bg-blue-600 hover:bg-blue-700 text-white">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children, required }) => (
  <div>
    <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">{label}{required && <span className="text-rose-500"> *</span>}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default Plans;
